import React, { useEffect, useState } from 'react'
import apiCall from '../helper/apiCall';
import OrderDetailsModal from './OrderDetailsModal';

const OrderView = () => {
    const [orders, setOrders] = useState([]);
    const [shippingStatuses, setShippingStatuses] = useState([]);
    const [statusFilter, setStatusFilter] = useState('All');
    const [dateFilter, setDateFilter] = useState('Today');
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(true);
    const [isDateRangeModalOpen, setIsDateRangeModalOpen] = useState(false);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedOrderId, setSelectedOrderId] = useState(null);

    const handleViewClick = (orderId) => {
        setSelectedOrderId(orderId);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedOrderId(null);
    };

    // Refactored useEffect for fetching shipping statuses only once on mount.
    // The empty dependency array `[]` ensures this effect runs only once.
    useEffect(() => {
        const fetchShippingStatuses = async () => {
            setLoading(true);
            try {
                const response = await apiCall("getShippingStatuses");
                if (response?.success) {
                    setShippingStatuses(response?.data);
                }
            } catch (error) {
                console.error("Failed to fetch shipping statuses:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchShippingStatuses();
    }, []);

    // Refactored useEffect for fetching orders. The dependencies are now correct,
    // and the `try...catch...finally` block ensures the loading state is always handled.
    useEffect(() => {
        const fetchOrders = async () => {
            setLoading(true);
            try {
                const response = await apiCall("getOrders", { dateFilter, startDate, endDate, statusFilter, searchQuery });
                if (response?.success) {
                    setOrders(response?.data);
                } else {
                    setOrders([]); // Clear orders on a failed request
                }
            } catch (error) {
                console.error("Failed to fetch orders:", error);
                setOrders([]); // Clear orders on a caught error
            } finally {
                setLoading(false);
            }
        };
        fetchOrders();
    }, [dateFilter, startDate, endDate, statusFilter, searchQuery]);

    // const getStatusText = (statusId) => {
    //     switch (statusId) {
    //         case 1:
    //             return 'Order Placed';
    //         case 2:
    //             return 'In Transit';
    //         case 3:
    //             return 'Delivered';
    //         case 4:
    //             return 'Returned';
    //         case 5:
    //             return 'Cancelled';
    //         case 6:
    //             return 'Shipped';
    //         default:
    //             return 'Unknown';
    //     }
    // };

    const getStatusText = (statusId) => {
        switch (statusId) {
            case 10:
                return 'Order Placed';
            case 11:
                return 'Ready for shipping label.';
            case 12:
                return 'The Package has been shipped.';
            case 4:
                return 'Delivered';
            default:
                return 'Unknown';
        }
    };

    const getOrderStatusColor = (statusId) => {
    switch (statusId) {
        case 10:
            return 'bg-yellow-100 text-yellow-800';
        case 11:
            return 'bg-blue-100 text-blue-800';
        case 4:
            return 'bg-green-100 text-green-800';
        case 12:
            return 'bg-red-100 text-red-800';
        case 5:
            return 'bg-gray-100 text-gray-800';
        case 6:
            return 'bg-cyan-100 text-cyan-800';
        default:
            return 'bg-gray-100 text-gray-800';
    }
};

    const handleViewDetails = (order) => {
        // Original function, can be kept as is or removed if handleViewClick is used instead.
    };

    const handleDateRangeApply = () => {
        setDateFilter('Custom Range');
        setIsDateRangeModalOpen(false);
    };


    return (
        <div className="p-4 sm:p-6 lg:p-8 font-sans antialiased text-gray-800 bg-gray-50 min-h-screen">
            <div className="flex flex-col md:flex-row justify-between items-center mb-4 gap-2">
                <h1 className="text-2xl font-extrabold">Order Management</h1>
                <input
                    type="text"
                    placeholder="Search by product name..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full md:w-fit px-3 py-1 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200 shadow-sm text-sm"
                />
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 mb-6 flex flex-wrap gap-2">
                <div className="flex-1 min-w-[200px]">
                    <h3 className="text-sm font-semibold text-gray-600 mb-1">Status</h3>
                    <div className="flex flex-wrap gap-2">
                        {['All', 'Order Placed','Shipped', 'In Transit', 'Delivered','Returned','Cancelled' ].map(status => (
                            <button
                                key={status}
                                onClick={() => setStatusFilter(status)}
                                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors duration-200 ${statusFilter === status
                                    ? 'bg-blue-600 text-white shadow-sm'
                                    : 'bg-gray-100 text-gray-700 hover:bg-blue-100 hover:text-blue-700'
                                    }`}
                            >
                                {status}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="flex-1 min-w-[200px]">
                    <h3 className="text-sm font-semibold text-gray-600 mb-1">Timeline</h3>
                    <div className="flex flex-wrap gap-2">
                        {['Today', 'Yesterday', 'Last 7 Days', 'All Time', 'Last 30 Days', 'Last 6 Months', 'Custom Range'].map(date => (
                            <button
                                key={date}
                                onClick={() => {
                                    if (date === 'Custom Range') {
                                        setIsDateRangeModalOpen(true);
                                    } else {
                                        setDateFilter(date);
                                    }
                                }}
                                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors duration-200 ${dateFilter === date
                                    ? 'bg-blue-600 text-white shadow-sm'
                                    : 'bg-gray-100 text-gray-700 hover:bg-blue-100 hover:text-blue-700'
                                    }`}
                            >
                                {date}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="text-center py-8 text-gray-500">Loading orders...</div>
            ) : orders.length === 0 ? (
                <div className="text-center py-8 text-gray-500">No orders found matching your criteria.</div>
            ) : (
                <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Serial No
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Order No
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Order Date
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Amount
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Quantity
                                    </th>
                                
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Order Status
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {orders.map((order, index) => (
                                    <tr key={order.order_id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{index + 1}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{order.order_number}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{new Date(order.order_date).toLocaleDateString()}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{order.final_amount_INR}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{order.total_quanitiy}</td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getOrderStatusColor(order.order_status_id)}`}>
                                                {getStatusText(order.shipping_status_id)}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                            <button
                                                onClick={() => handleViewClick(order.order_id)}
                                                className="text-blue-600 hover:text-blue-900 transition-colors"
                                            >
                                                View
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {isDateRangeModalOpen && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center z-50">
                    <div className="relative p-6 bg-white rounded-xl shadow-lg w-11/12 md:w-1/3">
                        <div className="flex justify-between items-center mb-4 border-b pb-2">
                            <h3 className="text-xl font-bold text-gray-900">Select Date Range</h3>
                            <button onClick={() => setIsDateRangeModalOpen(false)} className="text-gray-400 hover:text-gray-600 transition-colors">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <div className="flex flex-col gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                                <input
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-sm"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                                <input
                                    type="date"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-sm"
                                />
                            </div>
                            <div className="flex justify-end gap-2 mt-4">
                                <button
                                    onClick={() => setIsDateRangeModalOpen(false)}
                                    className="px-4 py-2 text-sm font-medium rounded-lg bg-gray-200 text-gray-800 hover:bg-gray-300 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleDateRangeApply}
                                    disabled={!startDate || !endDate}
                                    className="px-4 py-2 text-sm font-medium rounded-lg bg-blue-600 text-white shadow-sm hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Apply
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <OrderDetailsModal
                orderId={selectedOrderId}
                show={isModalOpen}
                onClose={handleCloseModal}
                shippingStatuses={shippingStatuses}
            />
        </div>
    );
};

export default OrderView